import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Search, ShoppingCart, User, X, Plus, Minus,
  Phone, Mail, Eye, EyeOff, ChevronRight,
  Package, Heart, LogOut, ChevronLeft, ArrowRight,
  Truck, Shield, Clock, Star, Menu
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportBaba = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [authTab, setAuthTab] = useState("login");
  const [currentPage, setCurrentPage] = useState("home");
  const [accountTab, setAccountTab] = useState("dashboard");
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [storeSettings, setStoreSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [productModal, setProductModal] = useState(null);
  const [productVariants, setProductVariants] = useState([]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [showMiniCart, setShowMiniCart] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({
    firstName: "", lastName: "", email: "", password: "",
    phone: "", businessName: "", businessAddress: "",
    city: "", state: "", country: "United States", zipCode: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [shopPage, setShopPage] = useState(1);
  const PRODUCTS_PER_PAGE = 20;
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const productCarouselRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const imgs = storeSettings?.banner_images || [];
    if (imgs.length === 0) return;
    const interval = setInterval(() => {
      setBannerIndex(prev => (prev + 1) % imgs.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [storeSettings]);

  useEffect(() => {
    loadProducts(); loadStoreSettings(); loadBrands();
    const savedToken = localStorage.getItem("importbaba_token");
    const savedUser = localStorage.getItem("importbaba_user");
    const savedCart = localStorage.getItem("importbaba_cart");
    const savedWishlist = localStorage.getItem("importbaba_wishlist");
    if (savedToken && savedUser) { setToken(savedToken); setUser(JSON.parse(savedUser)); setIsLoggedIn(true); }
    if (savedCart) setCart(JSON.parse(savedCart));
    if (savedWishlist) setWishlist(JSON.parse(savedWishlist));
  }, []);

  useEffect(() => {
    if (isLoggedIn && token) {
      axios.get(`${API}/storefront/orders?limit=50`).then(res => {
        setOrders((res.data.orders || []).map(o => ({ ...o, id: o.order_id, date: o.created_at })));
      }).catch(() => {});
    }
  }, [isLoggedIn, token]);

  useEffect(() => { localStorage.setItem("importbaba_cart", JSON.stringify(cart)); }, [cart]);

  const loadProducts = async () => { try { const res = await axios.get(`${API}/supplier-products?limit=500`); setProducts(res.data.products || []); } catch {} };
  const loadBrands = async () => { try { const res = await axios.get(`${API}/supplier-products/brands`); setBrands(res.data.brands || []); } catch {} };
  const loadStoreSettings = async () => { try { const res = await axios.get(`${API}/store-settings`); setStoreSettings(res.data); } catch {} };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) { toast.error("Please enter email and password"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/storefront/auth/login`, { email: loginForm.email, password: loginForm.password });
      const { token: t, user: u } = res.data;
      setToken(t); setUser(u); setIsLoggedIn(true);
      localStorage.setItem("importbaba_token", t); localStorage.setItem("importbaba_user", JSON.stringify(u));
      toast.success("Welcome back!"); setCurrentPage("shop");
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid credentials"); }
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerForm.email || !registerForm.password || !registerForm.firstName || !registerForm.lastName) { toast.error("Please fill required fields"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/storefront/auth/register`, {
        first_name: registerForm.firstName, last_name: registerForm.lastName, email: registerForm.email,
        password: registerForm.password, phone: registerForm.phone, business_name: registerForm.businessName,
        business_address: registerForm.businessAddress, city: registerForm.city, state: registerForm.state,
        country: registerForm.country, zip_code: registerForm.zipCode,
      });
      const { token: t, user: u } = res.data;
      setToken(t); setUser(u); setIsLoggedIn(true);
      localStorage.setItem("importbaba_token", t); localStorage.setItem("importbaba_user", JSON.stringify(u));
      toast.success("Account created!"); setCurrentPage("shop");
    } catch (err) { toast.error(err.response?.data?.detail || "Registration failed"); }
    setLoading(false);
  };

  const handleLogout = () => {
    setUser(null); setToken(null); setIsLoggedIn(false);
    localStorage.removeItem("importbaba_user"); localStorage.removeItem("importbaba_token");
    setCurrentPage("home"); toast.success("Logged out");
  };

  const addToCart = (product, quantity = 1) => {
    setCart(prev => {
      const existing = prev.find(i => i.sku === product.sku);
      if (existing) return prev.map(i => i.sku === product.sku ? { ...i, quantity: i.quantity + quantity } : i);
      return [...prev, { ...product, quantity }];
    });
    toast.success("Added to cart");
  };

  const updateCartQuantity = (sku, q) => { if (q < 1) { removeFromCart(sku); return; } setCart(prev => prev.map(i => i.sku === sku ? { ...i, quantity: q } : i)); };
  const removeFromCart = (sku) => setCart(prev => prev.filter(i => i.sku !== sku));
  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.quantity, 0), [cart]);

  const toggleWishlist = (product) => {
    const exists = wishlist.find(p => p.sku === product.sku);
    const updated = exists ? wishlist.filter(p => p.sku !== product.sku) : [...wishlist, product];
    setWishlist(updated); localStorage.setItem("importbaba_wishlist", JSON.stringify(updated));
    toast.success(exists ? "Removed from wishlist" : "Added to wishlist");
  };

  const openProductModal = (product) => { setProductModal(product); setProductVariants(product.variants || []); };

  const filteredProducts = useMemo(() => products.filter(p => {
    const matchSearch = !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku?.toLowerCase().includes(searchQuery.toLowerCase()) || p.brand?.toLowerCase().includes(searchQuery.toLowerCase()) || p.variants?.some(v => v.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchBrand = selectedBrand === "all" || p.brand?.toLowerCase() === selectedBrand.toLowerCase();
    return matchSearch && matchBrand;
  }), [products, searchQuery, selectedBrand]);

  useEffect(() => { setShopPage(1); }, [searchQuery, selectedBrand]);
  const totalShopPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const paginatedProducts = filteredProducts.slice((shopPage - 1) * PRODUCTS_PER_PAGE, shopPage * PRODUCTS_PER_PAGE);

  const topBrands = useMemo(() => {
    const grouped = {};
    products.forEach(p => {
      const brand = p.brand || "";
      if (!brand) return;
      if (!grouped[brand]) grouped[brand] = [];
      grouped[brand].push(p);
    });
    return Object.entries(grouped)
      .sort((a, b) => b[1].length - a[1].length)
      .map(([name, items]) => ({
        name, count: items.length,
        image: items[0]?.image_url || "",
        hasVariants: items.some(p => p.variants?.length > 0),
        priceRange: items.filter(p => p.price > 0).length > 0
          ? `$${Math.min(...items.filter(p => p.price > 0).map(p => p.price)).toFixed(0)} - $${Math.max(...items.filter(p => p.price > 0).map(p => p.price)).toFixed(0)}`
          : "Contact",
        topProduct: items[0],
      }));
  }, [products]);

  const s = storeSettings || {};
  const bannerImages = s.banner_images || [];

  const featuredProducts = useMemo(() => {
    const withVariants = products.filter(p => p.image_url && p.variants?.length > 0);
    const rest = products.filter(p => p.image_url && (!p.variants || p.variants.length === 0));
    return [...withVariants, ...rest].slice(0, 16);
  }, [products]);

  const scrollCarousel = useCallback((dir) => {
    if (!productCarouselRef.current) return;
    const w = productCarouselRef.current.clientWidth;
    productCarouselRef.current.scrollBy({ left: dir === "left" ? -w * 0.8 : w * 0.8, behavior: "smooth" });
  }, []);

  // Extract specs from product name
  const getSpecs = (product) => {
    const specs = [];
    const name = product.name || "";
    const mgMatch = name.match(/(\d+)\s*[Mm][Gg]/);
    const ctMatch = name.match(/(\d+)\s*[Cc][Tt]/);
    const pkMatch = name.match(/(\d+)\s*[Pp][Kk]/);
    if (mgMatch) specs.push(`${mgMatch[1]}mg Per Tablet`);
    if (ctMatch) specs.push(`${ctMatch[1]} Count`);
    if (pkMatch) specs.push(`${pkMatch[1]} Pack`);
    if (product.variants?.length > 1) specs.push(`${product.variants.length} Flavors Available`);
    if (product.price > 0) specs.push(`From $${product.price.toFixed(2)}`);
    return specs.slice(0, 3);
  };

  // ============================================
  // HEADER
  // ============================================
  const Header = () => (
    <header className="fixed top-0 left-0 right-0 z-50" data-testid="header">
      <div className={`transition-all duration-500 ${scrolled ? "bg-white/90 backdrop-blur-2xl shadow-sm border-b border-zinc-200" : "bg-white/70 backdrop-blur-xl"}`}>
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-[72px]">
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => setCurrentPage("home")} data-testid="logo-btn">
              <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                <span className="text-white font-black text-base tracking-tighter" style={{ fontFamily: 'Manrope' }}>IB</span>
              </div>
              <div>
                <h1 className="text-xl font-black text-zinc-900 tracking-tighter leading-none uppercase" style={{ fontFamily: 'Manrope' }}>ImportBaba</h1>
                <p className="text-[9px] text-zinc-400 tracking-[0.3em] uppercase font-bold">Wholesale Hub</p>
              </div>
            </div>

            <nav className="hidden lg:flex items-center gap-10">
              {[
                { label: "Home", page: "home" },
                { label: "Products", page: "shop" },
                { label: "Categories", page: "home", scrollTo: "categories" },
              ].map(item => (
                <button key={item.label} onClick={() => {
                  setCurrentPage(item.page);
                  if (item.scrollTo) setTimeout(() => document.getElementById(item.scrollTo)?.scrollIntoView({ behavior: "smooth" }), 100);
                }}
                  className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors duration-300 font-bold tracking-wide uppercase">
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              <div className="hidden md:flex">
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
                  <input type="text" placeholder="Search..." value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); if (currentPage !== "shop") setCurrentPage("shop"); }}
                    className="w-44 pl-10 pr-4 py-2 bg-zinc-100 border border-zinc-200 rounded-full text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:bg-white focus:border-zinc-300 focus:w-64 transition-all duration-500"
                    data-testid="search-input" />
                </div>
              </div>

              <button onClick={() => { setCurrentPage("account"); setAccountTab("wishlist"); }}
                className="relative p-2.5 text-zinc-400 hover:text-zinc-900 transition-colors rounded-full hover:bg-zinc-100" data-testid="wishlist-btn">
                <Heart size={20} />
                {wishlist.length > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-zinc-900 text-white text-[10px] font-black rounded-full flex items-center justify-center">{wishlist.length}</span>}
              </button>

              {isLoggedIn ? (
                <button onClick={() => setCurrentPage("account")} className="hidden md:flex items-center" data-testid="account-btn">
                  <div className="w-8 h-8 bg-zinc-900 text-white rounded-full flex items-center justify-center font-bold text-xs">
                    {user?.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                </button>
              ) : (
                <button onClick={() => setCurrentPage("account")} className="hidden md:flex items-center gap-2 px-5 py-2 text-sm font-bold text-zinc-600 hover:text-zinc-900 border border-zinc-300 rounded-full hover:bg-zinc-50 transition-all duration-300 tracking-wide uppercase" data-testid="signin-btn">
                  <User size={14} />Sign In
                </button>
              )}

              <div className="relative">
                <button onClick={() => setShowMiniCart(!showMiniCart)}
                  className="flex items-center gap-2 px-5 py-2 bg-zinc-900 text-white rounded-full hover:scale-105 transition-transform duration-300 active:scale-95 text-sm font-black tracking-tight" data-testid="cart-btn">
                  <ShoppingCart size={16} />
                  <span className="hidden md:inline">${cartTotal.toFixed(2)}</span>
                  {cartCount > 0 && <span className="w-5 h-5 bg-white text-zinc-900 text-[10px] font-black rounded-full flex items-center justify-center">{cartCount}</span>}
                </button>
                <MiniCart />
              </div>

              <button onClick={() => setMobileMenu(!mobileMenu)} className="lg:hidden p-2 text-zinc-500 hover:text-zinc-900"><Menu size={22} /></button>
            </div>
          </div>
        </div>
      </div>

      {mobileMenu && (
        <div className="lg:hidden bg-white/95 backdrop-blur-2xl border-b border-zinc-200 px-6 py-5 space-y-3 shadow-lg">
          <div className="relative mb-3">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" size={16} />
            <input type="text" placeholder="Search..." value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage("shop"); setMobileMenu(false); }}
              className="w-full pl-10 pr-4 py-2.5 bg-zinc-100 border border-zinc-200 rounded-full text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none" />
          </div>
          {[{ label: "Home", page: "home" }, { label: "Products", page: "shop" }].map(({ label, page }) => (
            <button key={label} onClick={() => { setCurrentPage(page); setMobileMenu(false); }}
              className="block w-full text-left text-zinc-500 hover:text-zinc-900 py-2.5 text-sm font-bold uppercase tracking-wide">{label}</button>
          ))}
          {!isLoggedIn && <button onClick={() => { setCurrentPage("account"); setMobileMenu(false); }} className="block w-full text-left text-zinc-500 hover:text-zinc-900 py-2.5 text-sm font-bold uppercase tracking-wide">Sign In</button>}
          {isLoggedIn && <button onClick={() => { setCurrentPage("account"); setMobileMenu(false); }} className="block w-full text-left text-zinc-500 hover:text-zinc-900 py-2.5 text-sm font-bold uppercase tracking-wide">My Account</button>}
        </div>
      )}
    </header>
  );

  // ============================================
  // MINI CART
  // ============================================
  const MiniCart = () => {
    if (!showMiniCart) return null;
    return (
      <div className="absolute right-0 top-full mt-3 w-[400px] bg-white rounded-2xl shadow-2xl border border-zinc-200 z-50" data-testid="mini-cart">
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-black text-lg text-zinc-900 uppercase tracking-tight" style={{ fontFamily: 'Manrope' }}>Cart ({cartCount})</h3>
            <button onClick={() => setShowMiniCart(false)} className="text-zinc-400 hover:text-zinc-900 p-1"><X size={18} /></button>
          </div>
          {cart.length > 0 ? (
            <>
              <div className="max-h-64 overflow-y-auto space-y-2.5 pr-1">
                {cart.map(item => (
                  <div key={item.sku} className="flex gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-100">
                    <div className="w-14 h-14 bg-zinc-100 rounded-lg flex-shrink-0 overflow-hidden p-1">
                      {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-contain" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-zinc-900 truncate">{item.name}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">{item.quantity} x ${item.price?.toFixed(2)}</p>
                    </div>
                    <button onClick={() => removeFromCart(item.sku)} className="text-zinc-300 hover:text-red-400"><X size={14} /></button>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100">
                <div className="flex justify-between font-black text-lg mb-4 text-zinc-900"><span>Total</span><span>${cartTotal.toFixed(2)}</span></div>
                <button onClick={() => { setCurrentPage("cart"); setShowMiniCart(false); }}
                  className="w-full py-3 bg-zinc-900 text-white rounded-full font-black text-sm hover:scale-[1.02] transition-transform active:scale-[0.98] uppercase tracking-wide">
                  View Cart
                </button>
              </div>
            </>
          ) : <p className="text-zinc-400 text-center py-8">Your cart is empty</p>}
        </div>
      </div>
    );
  };

  // ============================================
  // HERO — Full screen carousel
  // ============================================
  const HeroBanner = () => (
    <div className="relative w-full h-[85vh] bg-zinc-100 overflow-hidden" data-testid="hero-banner">
      {bannerImages.length > 0 ? (
        <>
          <AnimatePresence>
            <motion.img key={bannerIndex} src={bannerImages[bannerIndex % bannerImages.length]} alt={`Slide ${bannerIndex + 1}`}
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8 }}
              className="absolute inset-0 w-full h-full object-cover"
              onError={e => { e.target.style.display = 'none'; }} />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-white/20" />

          <div className="absolute bottom-28 left-0 right-0 text-center z-10">
            <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}
              onClick={() => setCurrentPage("shop")}
              className="px-12 py-4 bg-zinc-900 text-white rounded-full font-black text-sm hover:scale-105 transition-transform duration-300 active:scale-95 tracking-widest uppercase shadow-xl" data-testid="explore-products-btn">
              Explore Products <ArrowRight size={16} className="inline ml-2" />
            </motion.button>
          </div>

          <button onClick={() => setBannerIndex(prev => (prev - 1 + bannerImages.length) % bannerImages.length)}
            className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 hover:bg-white text-zinc-700 rounded-full flex items-center justify-center backdrop-blur-xl transition-all border border-zinc-200 hover:scale-110 z-10 shadow-lg"
            data-testid="hero-prev-btn"><ChevronLeft size={22} /></button>
          <button onClick={() => setBannerIndex(prev => (prev + 1) % bannerImages.length)}
            className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/80 hover:bg-white text-zinc-700 rounded-full flex items-center justify-center backdrop-blur-xl transition-all border border-zinc-200 hover:scale-110 z-10 shadow-lg"
            data-testid="hero-next-btn"><ChevronRight size={22} /></button>

          <div className="absolute bottom-14 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {bannerImages.slice(0, 12).map((_, i) => (
              <button key={i} onClick={() => setBannerIndex(i)}
                className={`rounded-full transition-all duration-500 ${i === bannerIndex % bannerImages.length ? "bg-zinc-900 w-8 h-2" : "bg-zinc-400/40 w-2 h-2 hover:bg-zinc-400"}`}
                data-testid={`hero-dot-${i}`} />
            ))}
          </div>
        </>
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-zinc-50">
          <div className="text-center">
            <h1 className="text-6xl md:text-[10vw] font-black text-zinc-900 tracking-tighter uppercase leading-[0.85]" style={{ fontFamily: 'Manrope' }}>ImportBaba</h1>
            <p className="text-zinc-400 mt-6 text-lg tracking-[0.3em] uppercase font-bold">Premium Wholesale Products</p>
          </div>
        </div>
      )}
    </div>
  );

  // ============================================
  // PRODUCTS CAROUSEL — Large landscape cards like GeekBar
  // ============================================
  const ProductsCarousel = () => {
    if (featuredProducts.length === 0) return null;

    return (
      <div className="bg-white py-20 relative" data-testid="products-carousel">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-zinc-900 tracking-tighter uppercase" style={{ fontFamily: 'Manrope' }}>Products</h2>
            <div className="flex gap-3">
              <button onClick={() => scrollCarousel("left")}
                className="w-12 h-12 bg-zinc-100 hover:bg-zinc-200 text-zinc-500 hover:text-zinc-900 rounded-full flex items-center justify-center border border-zinc-200 transition-all hover:scale-110">
                <ChevronLeft size={20} />
              </button>
              <button onClick={() => scrollCarousel("right")}
                className="w-12 h-12 bg-zinc-100 hover:bg-zinc-200 text-zinc-500 hover:text-zinc-900 rounded-full flex items-center justify-center border border-zinc-200 transition-all hover:scale-110">
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>

        <div ref={productCarouselRef}
          className="flex gap-6 overflow-x-auto pl-6 lg:pl-10 pr-6 pb-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {featuredProducts.map((product, idx) => {
            const specs = getSpecs(product);
            return (
              <motion.div key={product.sku}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: idx * 0.05, duration: 0.5 }}
                onClick={() => openProductModal(product)}
                className="group flex-shrink-0 w-[85vw] sm:w-[60vw] md:w-[45vw] lg:w-[600px] xl:w-[700px] relative rounded-2xl overflow-hidden cursor-pointer snap-center bg-zinc-50 border border-zinc-200 hover:border-zinc-300 hover:shadow-xl transition-all duration-500"
                data-testid="carousel-product-card">
                <div className="relative aspect-[2/1] bg-gradient-to-br from-zinc-100 to-zinc-50 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/50 to-transparent z-10" />
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name}
                      className="absolute right-4 top-1/2 -translate-y-1/2 h-[85%] object-contain group-hover:scale-110 transition-transform duration-700 z-0" />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-zinc-300"><Package size={80} /></div>
                  )}
                  <div className="absolute left-8 top-1/2 -translate-y-1/2 z-20 max-w-[55%]">
                    {product.brand && <p className="text-xs font-bold text-zinc-400 uppercase tracking-[0.2em] mb-2">{product.brand}</p>}
                    <h3 className="text-2xl md:text-3xl font-black text-zinc-900 tracking-tight uppercase leading-[1.1] mb-4" style={{ fontFamily: 'Manrope' }}>
                      {product.name?.replace(/\d+[Mm][Gg].*/, '').trim() || product.name}
                    </h3>
                    <div className="space-y-2">
                      {specs.map((spec, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className="w-1 h-1 bg-zinc-400 rounded-full" />
                          <p className="text-sm text-zinc-500 font-medium">{spec}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    );
  };

  // ============================================
  // ALL CATEGORIES — Grid of all brands
  // ============================================
  const AllCategories = () => {
    if (topBrands.length === 0) return null;
    return (
      <div id="categories" className="bg-zinc-50 py-20" data-testid="all-categories">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="text-xs font-bold text-zinc-400 tracking-[0.3em] uppercase mb-3">Browse By</p>
              <h2 className="text-4xl md:text-5xl font-black text-zinc-900 tracking-tighter uppercase" style={{ fontFamily: 'Manrope' }}>All Categories</h2>
              <p className="text-zinc-400 mt-2 text-sm">{topBrands.length} brands &middot; {products.length} products</p>
            </div>
            <button onClick={() => { setSelectedBrand("all"); setCurrentPage("shop"); }}
              className="px-6 py-2.5 bg-zinc-900 text-white rounded-full text-xs font-bold hover:scale-105 transition-all uppercase tracking-wide">
              View All <ArrowRight size={14} className="inline ml-1" />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {topBrands.map((brand, idx) => (
              <motion.div key={brand.name}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: Math.min(idx * 0.02, 0.3), duration: 0.4 }}
                onClick={() => { setSelectedBrand(brand.name); setCurrentPage("shop"); }}
                className="group relative bg-white rounded-2xl border border-zinc-200 hover:border-zinc-300 hover:shadow-lg transition-all duration-500 cursor-pointer overflow-hidden"
                data-testid="category-card">
                <div className="aspect-square bg-zinc-50 p-6 overflow-hidden relative">
                  {brand.image ? (
                    <img src={brand.image} alt={brand.name} className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-200"><Package size={40} /></div>
                  )}
                </div>
                <div className="p-4">
                  <h4 className="font-black text-zinc-900 text-sm truncate uppercase tracking-tight" style={{ fontFamily: 'Manrope' }}>{brand.name}</h4>
                  <p className="text-xs text-zinc-400 mt-1 font-bold">{brand.count} {brand.count === 1 ? "product" : "products"}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // TRUST SECTION
  // ============================================
  const TrustSection = () => (
    <div className="bg-white border-y border-zinc-200" data-testid="trust-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Truck, label: "Fast Shipping", desc: "2-5 business days" },
            { icon: Shield, label: "Verified Products", desc: "100% authentic" },
            { icon: Clock, label: "Business Hours", desc: s.business_hours?.mon_fri || "10am-7pm CST" },
            { icon: Star, label: "Wholesale Prices", desc: "Best rates guaranteed" },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="flex items-center gap-4 group">
              <div className="w-12 h-12 bg-zinc-100 text-zinc-400 group-hover:text-zinc-900 rounded-xl flex items-center justify-center flex-shrink-0 border border-zinc-200 group-hover:border-zinc-300 transition-all duration-500">
                <Icon size={22} />
              </div>
              <div>
                <p className="text-sm font-bold text-zinc-900 uppercase tracking-wide">{label}</p>
                <p className="text-xs text-zinc-400 mt-0.5">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ============================================
  // BRAND ABOUT
  // ============================================
  const BrandAbout = () => (
    <div className="bg-zinc-50 py-28" data-testid="brand-section">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <div className="w-24 h-24 bg-zinc-900 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-xl">
          <span className="text-white font-black text-4xl tracking-tighter" style={{ fontFamily: 'Manrope' }}>IB</span>
        </div>
        <h2 className="text-5xl md:text-6xl font-black text-zinc-900 mb-6 tracking-tighter uppercase" style={{ fontFamily: 'Manrope' }}>IMPORTBABA</h2>
        <p className="text-zinc-500 text-lg leading-relaxed max-w-xl mx-auto">
          Your trusted wholesale partner, offering premium products at competitive prices. We source directly from manufacturers to deliver the best quality products for your retail business.
        </p>
        <button onClick={() => setCurrentPage("shop")}
          className="mt-10 px-10 py-3.5 border border-zinc-300 text-zinc-900 rounded-full text-sm font-black hover:bg-zinc-900 hover:text-white transition-all duration-300 tracking-widest uppercase">
          VIEW MORE
        </button>
      </div>
    </div>
  );

  // ============================================
  // SOCIAL MARQUEE
  // ============================================
  const SocialMarquee = () => (
    <div className="bg-white py-4 border-y border-zinc-200" data-testid="social-marquee">
      <Marquee speed={50} gradient={false}>
        {Array(10).fill(null).map((_, i) => (
          <span key={i} className="mx-8 text-lg font-black text-transparent uppercase tracking-wider"
            style={{ WebkitTextStroke: '1px rgba(0,0,0,0.1)', fontFamily: 'Manrope' }}>
            Follow us @importbaba
          </span>
        ))}
      </Marquee>
    </div>
  );

  // ============================================
  // PRODUCT CARD — Shop grid
  // ============================================
  const ProductCard = ({ product }) => {
    const isWish = wishlist.find(p => p.sku === product.sku);
    const variantCount = product.variants?.length || 0;

    return (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="group relative bg-white rounded-2xl border border-zinc-200 hover:border-zinc-300 hover:shadow-lg transition-all duration-500 overflow-hidden" data-testid="product-card">
        <div className="relative cursor-pointer" onClick={() => openProductModal(product)}>
          <div className="aspect-square bg-zinc-50 p-6 overflow-hidden">
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-zinc-200"><Package size={48} /></div>
            )}
          </div>
          <button onClick={(e) => { e.stopPropagation(); toggleWishlist(product); }}
            className={`absolute top-4 right-4 p-2 rounded-full z-20 transition-all duration-300 ${isWish ? "bg-red-50 text-red-400" : "bg-white/80 backdrop-blur-md text-zinc-400 hover:text-zinc-900 opacity-0 group-hover:opacity-100 shadow-sm"}`}>
            <Heart size={16} fill={isWish ? "currentColor" : "none"} />
          </button>
          {product.brand && (
            <div className="absolute top-4 left-4 px-2.5 py-1 bg-white/80 backdrop-blur-md text-zinc-700 text-[10px] font-black rounded-full border border-zinc-200 uppercase tracking-wider z-20">
              {product.brand}
            </div>
          )}
          {variantCount > 1 && (
            <div className="absolute bottom-4 left-4 px-3 py-1 bg-white/80 backdrop-blur-md text-zinc-600 text-xs font-bold rounded-full border border-zinc-200 z-20">
              {variantCount} flavors
            </div>
          )}
        </div>

        <div className="p-4">
          <h3 className="font-bold text-zinc-900 line-clamp-2 min-h-[40px] text-sm leading-snug mb-2 cursor-pointer hover:text-zinc-600 tracking-tight"
            onClick={() => openProductModal(product)} style={{ fontFamily: 'Manrope' }}>
            {product.name}
          </h3>
          <div className="flex items-end justify-between mb-3">
            <span className="text-lg font-black text-zinc-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              {product.price > 0 ? `$${product.price.toFixed(2)}` : "Contact"}
            </span>
            {product.stock_quantity > 0 && (
              <span className="text-[10px] text-zinc-500 bg-zinc-100 px-2 py-0.5 rounded-full font-bold border border-zinc-200">{product.stock_quantity} in stock</span>
            )}
          </div>
          <button onClick={() => variantCount > 1 ? openProductModal(product) : addToCart(product)}
            className={`w-full py-2.5 rounded-full text-xs font-black uppercase tracking-wider transition-all duration-300 active:scale-[0.98] ${
              variantCount > 1
                ? "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 border border-zinc-200"
                : "bg-zinc-900 text-white hover:scale-[1.02]"
            }`} data-testid="add-to-cart-btn">
            {variantCount > 1 ? "Select Flavor" : "Add to Cart"}
          </button>
        </div>
      </motion.div>
    );
  };

  // ============================================
  // PRODUCT DETAIL MODAL
  // ============================================
  const ProductDetailModal = () => {
    if (!productModal) return null;
    return (
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={() => setProductModal(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-zinc-200 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="product-detail-modal">

            <div className="p-6 border-b border-zinc-100 flex items-center justify-between">
              <div>
                {productModal.brand && <p className="text-xs font-black text-zinc-400 uppercase tracking-wider mb-1">{productModal.brand}</p>}
                <h2 className="text-xl font-black text-zinc-900 tracking-tight uppercase" style={{ fontFamily: 'Manrope' }}>{productModal.name}</h2>
              </div>
              <button onClick={() => setProductModal(null)} className="p-2.5 hover:bg-zinc-100 rounded-full text-zinc-400 hover:text-zinc-900" data-testid="close-modal-btn"><X size={22} /></button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-8">
                <div className="aspect-square bg-zinc-50 rounded-2xl overflow-hidden p-10 border border-zinc-100">
                  {productModal.image_url ? (
                    <img src={productModal.image_url} alt={productModal.name} className="w-full h-full object-contain" />
                  ) : <div className="w-full h-full flex items-center justify-center text-zinc-200"><Package size={80} /></div>}
                </div>
                <div className="flex flex-col justify-center">
                  <span className="text-xs font-black text-zinc-400 uppercase tracking-[0.3em]">{productModal.category}</span>
                  <p className="text-sm text-zinc-400 mt-3 font-mono">SKU: {productModal.sku}</p>
                  {productModal.product_url && (
                    <a href={productModal.product_url} target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-500 hover:text-zinc-900 mt-2 inline-flex items-center gap-1 font-bold">
                      View on supplier <ArrowRight size={12} />
                    </a>
                  )}
                  <div className="mt-8 flex items-baseline gap-4">
                    <span className="text-5xl font-black text-zinc-900 tracking-tighter" style={{ fontFamily: 'Manrope' }}>
                      {productModal.price > 0 ? `$${productModal.price.toFixed(2)}` : "Contact for pricing"}
                    </span>
                    {productModal.stock_quantity > 0 && <span className="text-sm text-zinc-500 font-bold">{productModal.stock_quantity} in stock</span>}
                  </div>
                  {productVariants.length === 0 && (
                    <button onClick={() => { addToCart(productModal); setProductModal(null); }}
                      className="mt-8 px-10 py-3.5 bg-zinc-900 text-white rounded-full font-black hover:scale-105 transition-transform active:scale-95 uppercase tracking-wider text-sm">
                      Add to Cart
                    </button>
                  )}
                </div>
              </div>

              {productVariants.length > 0 && (
                <div>
                  <h3 className="text-lg font-black text-zinc-900 mb-5 uppercase tracking-tight" style={{ fontFamily: 'Manrope' }}>
                    Available Flavors <span className="text-zinc-400 font-bold text-base">({productVariants.length})</span>
                  </h3>
                  <div className="rounded-2xl border border-zinc-200 overflow-hidden" data-testid="variant-table">
                    <table className="w-full">
                      <thead><tr className="bg-zinc-50">
                        <th className="text-left p-4 text-xs font-black text-zinc-400 uppercase tracking-wider">Flavor</th>
                        <th className="text-left p-4 text-xs font-black text-zinc-400 uppercase tracking-wider">SKU</th>
                        <th className="text-left p-4 text-xs font-black text-zinc-400 uppercase tracking-wider">Price</th>
                        <th className="text-left p-4 text-xs font-black text-zinc-400 uppercase tracking-wider">Stock</th>
                        <th className="text-right p-4 text-xs font-black text-zinc-400 uppercase tracking-wider">Action</th>
                      </tr></thead>
                      <tbody>
                        {productVariants.map((v, i) => (
                          <tr key={v.sku} className={`border-t border-zinc-100 hover:bg-zinc-50 ${i % 2 === 0 ? "" : "bg-zinc-50/50"}`}>
                            <td className="p-4 font-bold text-sm text-zinc-900">{v.name}</td>
                            <td className="p-4 text-xs text-zinc-400 font-mono">{v.sku}</td>
                            <td className="p-4 text-sm font-black text-zinc-900">${v.price?.toFixed(2)}</td>
                            <td className="p-4"><span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${v.stock_quantity > 0 ? "bg-zinc-100 text-zinc-600 border border-zinc-200" : "bg-zinc-100 text-zinc-400 border border-zinc-100"}`}>{v.stock_quantity > 0 ? v.stock_quantity : "Out"}</span></td>
                            <td className="p-4 text-right">
                              <button onClick={() => { addToCart({ ...v, image_url: productModal.image_url, category: productModal.category }); setProductModal(null); }}
                                disabled={v.stock_quantity === 0}
                                className="px-5 py-2 bg-zinc-900 text-white rounded-full text-xs font-black hover:scale-105 transition-transform disabled:opacity-20 disabled:cursor-not-allowed active:scale-95 uppercase tracking-wider"
                                data-testid={`add-variant-${v.sku}`}>Add</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  // ============================================
  // AUTH PAGE
  // ============================================
  const AuthPage = () => (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4 bg-zinc-50">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-zinc-900 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <span className="text-white font-black text-2xl tracking-tighter" style={{ fontFamily: 'Manrope' }}>IB</span>
          </div>
          <h2 className="text-3xl font-black text-zinc-900 uppercase tracking-tighter" style={{ fontFamily: 'Manrope' }}>
            {authTab === "login" ? "Welcome back" : "Create account"}
          </h2>
          <p className="text-zinc-400 mt-3 text-sm">{authTab === "login" ? "Sign in to access wholesale pricing" : "Join our wholesale community"}</p>
        </div>

        <div className="flex bg-zinc-100 rounded-full p-1 mb-8 border border-zinc-200">
          <button onClick={() => setAuthTab("login")} className={`flex-1 py-2.5 rounded-full text-sm font-black transition-all uppercase tracking-wide ${authTab === "login" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`} data-testid="auth-login-tab">Sign In</button>
          <button onClick={() => setAuthTab("register")} className={`flex-1 py-2.5 rounded-full text-sm font-black transition-all uppercase tracking-wide ${authTab === "register" ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-zinc-900"}`} data-testid="auth-register-tab">Register</button>
        </div>

        {authTab === "login" ? (
          <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-black text-zinc-400 mb-2 uppercase tracking-wider">Email</label>
              <input type="text" value={loginForm.email} onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                className="w-full px-5 py-3 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-300 focus:border-zinc-300"
                placeholder="you@company.com" required data-testid="login-email" />
            </div>
            <div>
              <label className="block text-xs font-black text-zinc-400 mb-2 uppercase tracking-wider">Password</label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={loginForm.password} onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full px-5 py-3 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-300 pr-12"
                  placeholder="Enter password" required data-testid="login-password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-300 hover:text-zinc-500">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full py-3.5 bg-zinc-900 text-white rounded-full font-black hover:scale-[1.02] transition-transform active:scale-[0.98] disabled:opacity-50 uppercase tracking-wider text-sm">
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-[10px] font-black text-zinc-400 mb-1.5 uppercase tracking-wider">First Name *</label>
                <input type="text" value={registerForm.firstName} onChange={e => setRegisterForm({...registerForm, firstName: e.target.value})}
                  className="w-full px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-300" required data-testid="register-firstname" /></div>
              <div><label className="block text-[10px] font-black text-zinc-400 mb-1.5 uppercase tracking-wider">Last Name *</label>
                <input type="text" value={registerForm.lastName} onChange={e => setRegisterForm({...registerForm, lastName: e.target.value})}
                  className="w-full px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-300" required data-testid="register-lastname" /></div>
            </div>
            <div><label className="block text-[10px] font-black text-zinc-400 mb-1.5 uppercase tracking-wider">Business Email *</label>
              <input type="email" value={registerForm.email} onChange={e => setRegisterForm({...registerForm, email: e.target.value})}
                className="w-full px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-300" required data-testid="register-email" /></div>
            <div><label className="block text-[10px] font-black text-zinc-400 mb-1.5 uppercase tracking-wider">Password *</label>
              <input type="password" value={registerForm.password} onChange={e => setRegisterForm({...registerForm, password: e.target.value})}
                className="w-full px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-300" required data-testid="register-password" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-[10px] font-black text-zinc-400 mb-1.5 uppercase tracking-wider">Phone</label>
                <input type="text" value={registerForm.phone} onChange={e => setRegisterForm({...registerForm, phone: e.target.value})}
                  className="w-full px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-300" /></div>
              <div><label className="block text-[10px] font-black text-zinc-400 mb-1.5 uppercase tracking-wider">Business Name</label>
                <input type="text" value={registerForm.businessName} onChange={e => setRegisterForm({...registerForm, businessName: e.target.value})}
                  className="w-full px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-300" /></div>
            </div>
            <div><label className="block text-[10px] font-black text-zinc-400 mb-1.5 uppercase tracking-wider">Business Address</label>
              <input type="text" value={registerForm.businessAddress} onChange={e => setRegisterForm({...registerForm, businessAddress: e.target.value})}
                className="w-full px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-300" /></div>
            <div className="grid grid-cols-3 gap-3">
              <input type="text" placeholder="City" value={registerForm.city} onChange={e => setRegisterForm({...registerForm, city: e.target.value})} className="px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-300" />
              <input type="text" placeholder="State" value={registerForm.state} onChange={e => setRegisterForm({...registerForm, state: e.target.value})} className="px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-300" />
              <input type="text" placeholder="Zip" value={registerForm.zipCode} onChange={e => setRegisterForm({...registerForm, zipCode: e.target.value})} className="px-4 py-2.5 bg-white border border-zinc-200 rounded-xl text-sm text-zinc-900 placeholder:text-zinc-300 focus:outline-none focus:ring-2 focus:ring-zinc-300" />
            </div>
            <button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full py-3.5 bg-zinc-900 text-white rounded-full font-black hover:scale-[1.02] transition-transform active:scale-[0.98] disabled:opacity-50 uppercase tracking-wider text-sm">
              {loading ? "Creating..." : "Create Account"}
            </button>
          </form>
        )}
        <p className="text-center text-xs text-zinc-400 mt-8">Need help? Contact us at <span className="font-bold text-zinc-600">{s.phone || "+1 (630)422-1915"}</span></p>
      </div>
    </div>
  );

  // ============================================
  // SHOP PAGE
  // ============================================
  const ShopPage = () => (
    <div className="bg-white min-h-screen pt-28 pb-20">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
        <div className="mb-10">
          <p className="text-xs font-bold text-zinc-400 tracking-[0.3em] uppercase mb-3">Browse</p>
          <h1 className="text-4xl md:text-5xl font-black text-zinc-900 tracking-tighter uppercase" style={{ fontFamily: 'Manrope' }}>
            {selectedBrand !== "all" ? selectedBrand : "All Products"}
          </h1>
          <p className="text-zinc-400 mt-2 text-sm">{filteredProducts.length} products</p>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-4 mb-8" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <button onClick={() => setSelectedBrand("all")}
            className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap flex-shrink-0 ${selectedBrand === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500 border border-zinc-200 hover:bg-zinc-200"}`}>
            All ({products.length})
          </button>
          {brands.map(brand => (
            <button key={brand.name} onClick={() => setSelectedBrand(brand.name)}
              className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap flex-shrink-0 ${selectedBrand === brand.name ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500 border border-zinc-200 hover:bg-zinc-200"}`}>
              {brand.name} ({brand.count})
            </button>
          ))}
        </div>

        <div className="flex md:hidden w-full mb-6">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
            <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-zinc-100 border border-zinc-200 rounded-full text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-300" />
          </div>
        </div>

        {filteredProducts.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
              {paginatedProducts.map(p => <ProductCard key={p.sku} product={p} />)}
            </div>
            {totalShopPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-14">
                <button onClick={() => setShopPage(p => Math.max(1, p - 1))} disabled={shopPage === 1}
                  className="w-10 h-10 rounded-full bg-zinc-100 text-zinc-500 hover:text-zinc-900 border border-zinc-200 flex items-center justify-center disabled:opacity-30 transition-all hover:scale-110">
                  <ChevronLeft size={18} />
                </button>
                {Array.from({ length: Math.min(totalShopPages, 7) }, (_, i) => {
                  let pageNum;
                  if (totalShopPages <= 7) pageNum = i + 1;
                  else if (shopPage <= 4) pageNum = i + 1;
                  else if (shopPage >= totalShopPages - 3) pageNum = totalShopPages - 6 + i;
                  else pageNum = shopPage - 3 + i;
                  return (
                    <button key={pageNum} onClick={() => setShopPage(pageNum)}
                      className={`w-10 h-10 rounded-full text-sm font-black transition-all ${shopPage === pageNum ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-500 border border-zinc-200 hover:bg-zinc-200"}`}>
                      {pageNum}
                    </button>
                  );
                })}
                <button onClick={() => setShopPage(p => Math.min(totalShopPages, p + 1))} disabled={shopPage === totalShopPages}
                  className="w-10 h-10 rounded-full bg-zinc-100 text-zinc-500 hover:text-zinc-900 border border-zinc-200 flex items-center justify-center disabled:opacity-30 transition-all hover:scale-110">
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-24">
            <Package size={72} className="mx-auto text-zinc-200 mb-5" />
            <h3 className="text-xl font-black text-zinc-400 uppercase tracking-tight">No products found</h3>
            <p className="text-zinc-300 mt-2 text-sm">Try a different search or brand</p>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // CART PAGE
  // ============================================
  const CartPage = () => (
    <div className="bg-zinc-50 min-h-screen pt-28 pb-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <h1 className="text-4xl font-black text-zinc-900 mb-10 uppercase tracking-tighter" style={{ fontFamily: 'Manrope' }}>Shopping Cart</h1>
        {cart.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-4">
              {cart.map(item => (
                <div key={item.sku} className="flex gap-5 p-5 bg-white rounded-2xl border border-zinc-200 hover:shadow-md transition-all">
                  <div className="w-24 h-24 bg-zinc-50 rounded-xl flex-shrink-0 overflow-hidden p-2">
                    {item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-contain" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-zinc-900 text-sm truncate">{item.name}</h4>
                    <p className="text-xs text-zinc-400 mt-1 font-mono">{item.sku}</p>
                    <div className="flex items-center gap-5 mt-4">
                      <div className="flex items-center border border-zinc-200 rounded-full overflow-hidden">
                        <button onClick={() => updateCartQuantity(item.sku, item.quantity - 1)} className="px-3 py-2 hover:bg-zinc-100 text-zinc-500"><Minus size={14} /></button>
                        <span className="px-3 py-2 text-sm font-black text-zinc-900 min-w-[40px] text-center">{item.quantity}</span>
                        <button onClick={() => updateCartQuantity(item.sku, item.quantity + 1)} className="px-3 py-2 hover:bg-zinc-100 text-zinc-500"><Plus size={14} /></button>
                      </div>
                      <span className="text-xl font-black text-zinc-900 tracking-tight">${(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  </div>
                  <button onClick={() => removeFromCart(item.sku)} className="text-zinc-300 hover:text-red-400 self-start p-1"><X size={18} /></button>
                </div>
              ))}
            </div>
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl border border-zinc-200 p-8 sticky top-28 shadow-sm">
                <h3 className="font-black text-xl text-zinc-900 mb-6 uppercase tracking-tight" style={{ fontFamily: 'Manrope' }}>Summary</h3>
                <div className="space-y-4 text-sm">
                  <div className="flex justify-between text-zinc-400"><span>Subtotal ({cartCount})</span><span className="text-zinc-900 font-bold">${cartTotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-zinc-400"><span>Shipping</span><span className="text-zinc-600 font-bold">{cartTotal >= 500 ? "Free" : "Calculated"}</span></div>
                </div>
                <div className="mt-6 pt-6 border-t border-zinc-100 flex justify-between font-black text-2xl text-zinc-900"><span>Total</span><span>${cartTotal.toFixed(2)}</span></div>
                <button data-testid="checkout-btn"
                  onClick={async () => {
                    if (!isLoggedIn) { toast.error("Please sign in first"); setCurrentPage("account"); return; }
                    try {
                      const res = await axios.post(`${API}/storefront/orders`, {
                        customer: { name: user?.name || "Guest", email: user?.email || "", phone: user?.phone || "" },
                        items: cart.map(i => ({ sku: i.sku, name: i.name, price: i.price, quantity: i.quantity, image_url: i.image_url })),
                        total: cartTotal,
                      });
                      setCart([]); const o = res.data.order;
                      setOrders(prev => [{ ...o, id: o.order_id, date: o.created_at }, ...prev]);
                      toast.success("Order placed!"); setCurrentPage("account"); setAccountTab("orders");
                    } catch { toast.error("Failed to place order"); }
                  }}
                  className="w-full mt-8 py-4 bg-zinc-900 text-white rounded-full font-black hover:scale-[1.02] transition-transform active:scale-[0.98] uppercase tracking-wider text-sm">
                  Place Order
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-24">
            <ShoppingCart size={72} className="mx-auto text-zinc-200 mb-5" />
            <p className="text-zinc-400 mb-8 font-bold">Your cart is empty</p>
            <button onClick={() => setCurrentPage("shop")} className="px-8 py-3 bg-zinc-900 text-white rounded-full font-black hover:scale-105 transition-transform uppercase tracking-wider text-sm">Browse Products</button>
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // ACCOUNT PAGE
  // ============================================
  const AccountPage = () => (
    <div className="bg-zinc-50 min-h-screen pt-28 pb-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl border border-zinc-200 p-5 shadow-sm">
              <div className="flex items-center gap-4 mb-5 pb-5 border-b border-zinc-100">
                <div className="w-12 h-12 bg-zinc-900 text-white rounded-full flex items-center justify-center font-black text-lg">{user?.name?.charAt(0)?.toUpperCase() || "U"}</div>
                <div><p className="font-bold text-zinc-900 text-sm">{user?.name}</p><p className="text-xs text-zinc-400">{user?.email}</p></div>
              </div>
              <nav className="space-y-1">
                {[{ id: "dashboard", label: "Dashboard" }, { id: "orders", label: "Orders" }, { id: "wishlist", label: "Wishlist" }, { id: "addresses", label: "Addresses" }, { id: "password", label: "Settings" }].map(item => (
                  <button key={item.id} onClick={() => setAccountTab(item.id)}
                    className={`w-full text-left py-2.5 px-4 rounded-xl text-sm font-bold transition-all uppercase tracking-wide ${accountTab === item.id ? "bg-zinc-900 text-white" : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"}`}>
                    {item.label}
                  </button>
                ))}
                <button onClick={handleLogout} className="w-full text-left py-2.5 px-4 rounded-xl text-sm font-bold text-red-400 hover:bg-red-50 flex items-center gap-2 uppercase tracking-wide" data-testid="logout-btn"><LogOut size={14} />Logout</button>
              </nav>
            </div>
          </div>

          <div className="md:col-span-3">
            {accountTab === "dashboard" && (
              <div className="bg-white rounded-2xl border border-zinc-200 p-8 shadow-sm">
                <h2 className="text-2xl font-black text-zinc-900 mb-3 uppercase tracking-tight" style={{ fontFamily: 'Manrope' }}>Dashboard</h2>
                <p className="text-zinc-400">Welcome back, <strong className="text-zinc-900">{user?.name}</strong>.</p>
                <div className="grid grid-cols-2 gap-5 mt-8">
                  <div className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100"><p className="text-3xl font-black text-zinc-900">{orders.length}</p><p className="text-xs text-zinc-400 mt-1 uppercase tracking-wider font-bold">Orders</p></div>
                  <div className="bg-zinc-50 rounded-2xl p-6 border border-zinc-100"><p className="text-3xl font-black text-zinc-900">{wishlist.length}</p><p className="text-xs text-zinc-400 mt-1 uppercase tracking-wider font-bold">Wishlist</p></div>
                </div>
              </div>
            )}

            {accountTab === "orders" && (
              <div>
                <h2 className="text-2xl font-black text-zinc-900 mb-6 uppercase tracking-tight" style={{ fontFamily: 'Manrope' }}>Orders</h2>
                {orders.length > 0 ? orders.map(order => (
                  <div key={order.id} className="bg-white rounded-2xl border border-zinc-200 p-6 mb-4 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div><p className="font-black text-zinc-900 text-sm font-mono">{order.id}</p><p className="text-xs text-zinc-400 mt-1">{order.date ? new Date(order.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "-"}</p></div>
                      <span className="px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-wider bg-zinc-100 text-zinc-600 border border-zinc-200">{order.status}</span>
                    </div>
                    <div className="border-t border-zinc-100 pt-4 space-y-3">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-zinc-50 rounded-lg overflow-hidden flex-shrink-0 p-1">{item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-contain" />}</div>
                          <div className="flex-1 min-w-0"><p className="text-sm text-zinc-600 truncate">{item.name}</p></div>
                          <p className="text-sm text-zinc-400">{item.quantity} x ${item.price?.toFixed(2)}</p>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-zinc-100 pt-4 mt-4 flex justify-between"><span className="font-bold text-zinc-400 text-sm">Total</span><span className="font-black text-xl text-zinc-900">${order.total?.toFixed(2)}</span></div>
                  </div>
                )) : <p className="text-zinc-400 bg-white rounded-2xl border border-zinc-200 p-10 text-center font-bold shadow-sm">No orders yet.</p>}
              </div>
            )}

            {accountTab === "wishlist" && (
              <div>
                <h2 className="text-2xl font-black text-zinc-900 mb-6 uppercase tracking-tight" style={{ fontFamily: 'Manrope' }}>Wishlist</h2>
                {wishlist.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                    {wishlist.map(item => (
                      <div key={item.sku} className="bg-white rounded-2xl border border-zinc-200 p-5 hover:shadow-md transition-all">
                        <div className="aspect-square bg-zinc-50 rounded-xl mb-4 p-4 overflow-hidden">{item.image_url && <img src={item.image_url} alt="" className="w-full h-full object-contain" />}</div>
                        <p className="text-sm font-bold text-zinc-900 line-clamp-2">{item.name}</p>
                        <p className="font-black text-zinc-900 mt-2">{item.price > 0 ? `$${item.price.toFixed(2)}` : "Contact"}</p>
                        <div className="flex gap-2 mt-4">
                          <button onClick={() => addToCart(item)} className="flex-1 py-2 bg-zinc-900 text-white rounded-full text-xs font-black hover:scale-[1.02] transition-transform uppercase">Add to Cart</button>
                          <button onClick={() => toggleWishlist(item)} className="px-3 py-2 border border-zinc-200 rounded-full text-xs text-red-400 hover:bg-red-50 font-bold">Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-zinc-400 bg-white rounded-2xl border border-zinc-200 p-10 text-center font-bold shadow-sm">Your wishlist is empty.</p>}
              </div>
            )}

            {accountTab === "addresses" && (
              <div className="bg-white rounded-2xl border border-zinc-200 p-8 shadow-sm"><h2 className="text-2xl font-black text-zinc-900 mb-4 uppercase tracking-tight" style={{ fontFamily: 'Manrope' }}>Addresses</h2><p className="text-zinc-400">No saved addresses yet.</p></div>
            )}
            {accountTab === "password" && (
              <div className="bg-white rounded-2xl border border-zinc-200 p-8 shadow-sm"><h2 className="text-2xl font-black text-zinc-900 mb-6 uppercase tracking-tight" style={{ fontFamily: 'Manrope' }}>Settings</h2>
                <form className="max-w-md space-y-5">
                  <div><label className="block text-xs font-black text-zinc-400 mb-2 uppercase tracking-wider">Display Name</label><input type="text" defaultValue={user?.name} className="w-full px-5 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-300" /></div>
                  <div><label className="block text-xs font-black text-zinc-400 mb-2 uppercase tracking-wider">Email</label><input type="email" defaultValue={user?.email} className="w-full px-5 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-300" /></div>
                  <button type="submit" className="px-8 py-3 bg-zinc-900 text-white rounded-full font-black hover:scale-105 transition-transform uppercase tracking-wider text-sm">Save</button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================
  // FOOTER
  // ============================================
  const bh = s.business_hours || {};
  const Footer = () => (
    <footer className="bg-zinc-900" data-testid="footer">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                <span className="text-zinc-900 font-black text-base tracking-tighter" style={{ fontFamily: 'Manrope' }}>IB</span>
              </div>
              <div>
                <h3 className="text-xl font-black text-white tracking-tighter uppercase" style={{ fontFamily: 'Manrope' }}>ImportBaba</h3>
                <p className="text-[9px] text-zinc-500 tracking-[0.3em] uppercase font-bold">Wholesale Hub</p>
              </div>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed max-w-sm">Premium wholesale products at competitive prices. Your trusted partner for retail business supplies.</p>
          </div>
          <div>
            <h4 className="text-xs font-black text-zinc-400 mb-5 uppercase tracking-[0.2em]">Contact</h4>
            <div className="space-y-3 text-sm text-zinc-400">
              <p className="flex items-center gap-2"><Phone size={14} /> {s.phone || "+1 (630)422-1915"}</p>
              <p className="flex items-center gap-2"><Mail size={14} /> {s.email || "info@importbaba.com"}</p>
              <p className="text-xs mt-3">Mon-Fri: {bh.mon_fri || "10am-7pm CST"}</p>
              <p className="text-xs">Sat: {bh.saturday || "10am-6pm CST"}</p>
            </div>
          </div>
          <div>
            <h4 className="text-xs font-black text-zinc-400 mb-5 uppercase tracking-[0.2em]">Quick Links</h4>
            <div className="space-y-3">
              {["Products", "Account", "Cart"].map(link => (
                <button key={link} onClick={() => setCurrentPage(link.toLowerCase())} className="block text-sm text-zinc-400 hover:text-white transition-colors font-medium">{link}</button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="border-t border-zinc-800 py-6 text-center text-xs text-zinc-600">
        <p>&copy; {new Date().getFullYear()} ImportBaba. All rights reserved.</p>
      </div>
    </footer>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="bg-white min-h-screen text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Toaster position="top-right" theme="light" toastOptions={{ style: { background: '#fff', border: '1px solid #e4e4e7', color: '#18181b' } }} />
      {Header()}
      {ProductDetailModal()}

      {currentPage === "home" && (
        <>
          {HeroBanner()}
          {ProductsCarousel()}
          {AllCategories()}
          {TrustSection()}
          {BrandAbout()}
          {SocialMarquee()}
        </>
      )}
      {currentPage === "shop" && ShopPage()}
      {currentPage === "cart" && CartPage()}
      {currentPage === "account" && (isLoggedIn ? AccountPage() : AuthPage())}

      {Footer()}
    </div>
  );
};

export default ImportBaba;
